import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from 'initializers/types';
import createCachedSelector from 're-reselect';

import Schedule from 'features/scheduleBuilder/ScheduleBuilderView/resources/Schedule';

import { captureHydrationException } from '../helpers';
import { ScheduleFetchAction } from '../types';

import { PARENT_SLICE_NAME, SLICE_NAME } from './constants';
import {
  ShiftEvent,
  ShiftEventSerializedEntity,
  ShiftEventsState,
} from './types';

export const shiftEventsAdapter = createEntityAdapter<ShiftEvent>();

export const initialState: ShiftEventsState = {
  ...shiftEventsAdapter.getInitialState(),
  status: 'idle',
  error: undefined,
};

const getScheduleBuilderState = (state: RootState) =>
  state.get(PARENT_SLICE_NAME);

const getShiftsState = (state: RootState) =>
  getScheduleBuilderState(state)?.shifts || { ids: [], entities: {} };

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    removeAll: shiftEventsAdapter.removeAll,
  },
  extraReducers: builder => {
    builder.addCase(
      Schedule.resourceActionEvent('fetch'),
      (state: ShiftEventsState, action: ScheduleFetchAction) => {
        state.status = 'succeeded';
        if (action.payload?.included) {
          try {
            const shiftEvents = (
              action?.payload?.included as ShiftEventSerializedEntity[]
            )
              .filter(
                (entity: ShiftEventSerializedEntity) =>
                  entity.type === 'shiftEvent'
              )
              .map((entity: ShiftEventSerializedEntity) => ({
                id: entity.id,
                type: entity.type,
                createdAt: entity.attributes.createdAt,
                userName: entity.attributes.userName,
                action: entity.attributes.action,
                title: entity.attributes.title,
                ...Object.fromEntries(
                  Object.entries(entity.relationships || {}).map(
                    ([k, v]: [string, any]) => [k, v.data]
                  )
                ),
              }));
            shiftEventsAdapter.setAll(state, shiftEvents);
          } catch (error) {
            captureHydrationException(state, error, SLICE_NAME);
          }
        }
      }
    );
  },
});

export const { reducer, actions } = slice;

const { selectAll: selectAllShiftEvents } =
  shiftEventsAdapter.getSelectors<RootState>(
    state => getScheduleBuilderState(state)?.[SLICE_NAME] || initialState
  );

export const selectShiftEvents = createCachedSelector(
  (state: RootState) => getShiftsState(state),
  (state: RootState) => selectAllShiftEvents(state),
  (_: RootState, shiftId: EntityId) => shiftId,
  (shiftsState, allShiftEvents, shiftId) => {
    const shift = shiftsState.entities[shiftId];

    if (!shift?.shiftEvents) {
      return [];
    }

    const shiftEventIds = Array.isArray(shift.shiftEvents)
      ? shift.shiftEvents.map((rel: any) => rel.id)
      : shift.shiftEvents.data?.map((rel: any) => rel.id) || [];

    return shiftEventIds
      .map((id: EntityId) =>
        allShiftEvents.find((event: ShiftEvent) => event.id === id)
      )
      .filter(Boolean) as ShiftEvent[];
  }
)((_: RootState, shiftId: EntityId) => `selectShiftEvents-${shiftId}`);

export const selectShiftEventData = createCachedSelector(
  (state: RootState, props: { id: EntityId }) =>
    selectShiftEvents(state, props.id),
  (shiftEvents: ShiftEvent[]) =>
    shiftEvents
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )
      .map(({ title, action, userName, createdAt }) => ({
        title,
        subtitle: `${action} ${userName}`,
        date: createdAt,
      }))
)(
  (_: RootState, props: { id: EntityId }) => `selectShiftEventData-${props.id}`
);

export const selectShiftEventsFetchIsLoading = (state: RootState): boolean =>
  getScheduleBuilderState(state)?.[SLICE_NAME]?.status === 'pending';
